import { cn } from "@/lib/utils";
import { forwardRef, useCallback, useEffect, useRef, useState } from "react";
import { ToolbarProvider } from "./Button";

interface ToolbarPillProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  wrapperClassName?: string;
  /** `header`: as tall as the header's buttons (`size="header"`). */
  size?: "default" | "header";
}

const ToolbarPill = forwardRef<HTMLDivElement, ToolbarPillProps>(
  ({ className, children, wrapperClassName, size = "default", ...props }, outerRef) => {
    const scrollRef = useRef<HTMLDivElement>(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);

    const check = useCallback(() => {
      const el = scrollRef.current;
      if (!el) return;
      setCanScrollLeft(el.scrollLeft > 1);
      setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
    }, []);

    useEffect(() => {
      const el = scrollRef.current;
      if (!el) return;
      check();
      el.addEventListener("scroll", check, { passive: true });
      const ro = new ResizeObserver(check);
      ro.observe(el);
      return () => {
        el.removeEventListener("scroll", check);
        ro.disconnect();
      };
    }, [check]);

    return (
      <div
        ref={outerRef}
        className={cn(
          "relative min-w-0 overflow-clip bg-canvas border border-border px-0.5 rounded-full font-chrome box-border h-9 min-h-9 flex flex-row items-center",
          size === "header" && "h-10 min-h-10 md:h-9 md:min-h-9",
          className,
        )}
        {...props}
      >
        <div
          ref={scrollRef}
          className={cn(
            "flex items-center gap-px h-full overflow-x-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden",
            wrapperClassName,
          )}
        >
          <ToolbarProvider size={size}>{children}</ToolbarProvider>
        </div>
        {/* Left fade */}
        <div
          className={cn(
            "pointer-events-none absolute inset-y-0 left-0 w-10 rounded-l-[10px] bg-gradient-to-r from-canvas to-transparent transition-opacity duration-300",
            canScrollLeft ? "opacity-100" : "opacity-0",
          )}
        />
        {/* Right fade */}
        <div
          className={cn(
            "pointer-events-none absolute inset-y-0 right-0 w-10 rounded-r-[10px] bg-gradient-to-l from-canvas to-transparent transition-opacity duration-300",
            canScrollRight ? "opacity-100" : "opacity-0",
          )}
        />
      </div>
    );
  },
);
ToolbarPill.displayName = "ToolbarPill";

function ToolbarSeparator() {
  return <div className="w-px h-[18px] bg-border mx-[2px] flex-shrink-0" />;
}

export { ToolbarPill, ToolbarSeparator };
